import { AxiosResponse } from 'axios'
import { axiosInstance } from '~/src/config/config_axios'
import { makeQueryUrl } from '~/src/libs'

import {
  IQuery,
  IContactOrder,
  IContainerBalance,
  IContainerMemo,
  IContainersResponse,
  IContainerResponse,
  IContainerStatusResponse,
} from './schema/Container'
import { FileUploadProps } from '~/src/types/form'

export class ContainerRepository {
  /**
   * getContainers
   * コンテナ情報一覧を取得する
   * @param query
   */
  async getContainers(query: IQuery): Promise<IContainersResponse['data']> {
    const response = await axiosInstance.get(makeQueryUrl('/containers', query))
    return response.data
  }

  /**
   * getContainer
   * コンテナ情報を取得する
   * @param containerId {number} コンテナID
   */
  async getContainer(containerId: number): Promise<IContainerResponse['data']> {
    const response = await axiosInstance.get(`/containers/${containerId}`)
    return response.data
  }

  /**
   * getContainerStatuses
   * コンテナステータスを取得する
   * @param containerId {number} コンテナID
   */
  async getContainerStatuses(
    containerId: number
  ): Promise<AxiosResponse<IContainerStatusResponse>> {
    return await axiosInstance.get(`/containers/${containerId}/statuses`)
  }

  /**
   * patchContainerMemo
   * メモ情報を登録、更新する
   * @param containerId {number} コンテナID
   * @param note {IContainerMemo} フォームテキスト
   */
  async patchContainerMemo(containerId: number, note: IContainerMemo): Promise<void> {
    return await axiosInstance.patch(`/containers/${containerId}/memo`, note)
  }

  /**
   * patchContainerMemo
   * メモ情報を登録、更新する
   * @param containerId {number} コンテナID
   * @param balance {IContainerBalance} 残金データ
   */
  async patchContainerBalance(containerId: number, balance: IContainerBalance): Promise<void> {
    return await axiosInstance.patch(`/containers/${containerId}/balance`, balance)
  }

  /**
   * getContactOrder
   * ユーザーのコンタクト注文一覧を取得
   * @param userId {number} ユーザーid
   */
  async getContactOrder(userId: number): Promise<IContactOrder['data']> {
    const response = await axiosInstance.get(
      `/request-orders?userId=${userId}&excludeStatuses[]=400&excludeStatuses[]=1000`
    )
    return response.data
  }

  /**
   * getContainersImages
   * コンテナ画像取得
   * @param containerId {number} コンテナID
   */
  async getContainersImages(containerId: number): Promise<AxiosResponse<any>> {
    return await axiosInstance.get(`/containers/${containerId}/images`)
  }

  /**
   * postContainersImages
   * コンテナ画像登録
   * @param containerId {number} コンテナID
   */
  async postContainersImages(
    containerId: number,
    postData: {
      path: string
      filename: string
    }[]
  ): Promise<AxiosResponse<any>> {
    return await axiosInstance.post(`/containers/${containerId}/images`, postData)
  }

  /**
   * deleteContainersImages
   * コンテナ画像削除
   * @param containerId {number} コンテナID
   * @param ids {string[]} 削除対象画像idの配列
   */
  async deleteContainersImages(containerId: number, ids: string[]) {
    const query = ids
      .reduce((queryString: string, id: string, index: number) => {
        const str = queryString + `ids[${index}]=${id}&`
        return str
      }, '?')
      .slice(0, -1)
    return await axiosInstance.delete(`/containers/${containerId}/images/${query}`)
  }

  /**
   * postContainerFiles
   * @param containerId
   * @param fileType 1: PACKING_LIST 2: SURRENDER_BL 3: BILL 4: BL_DRAFT 5: IMAGE 6: OTHER
   * @param postData
   */
  async postContainerFiles(
    containerId: number,
    fileType: number,
    postData: {
      file: {
        filename: string
        path: string
      }
      name: 'blDraft' | 'surrenderBl' | 'packingList' | string
      displayOrder?: number
    }[]
  ): Promise<AxiosResponse<any>> {
    console.log('postContainerFiles', containerId, fileType, postData)
    const res = await axiosInstance.post(`/containers/${containerId}/files/${fileType}`, postData)
    console.log('post', res)
    return res
  }

  /**
   * patchContainerFileName
   * @param containerFileId
   * @param name
   */
  async patchContainerFileName(containerFileId: number, name: string) {
    return await axiosInstance.patch(`/containers/files/${containerFileId}/name`, { name })
  }

  /**
   * deleteContainerFiles
   * @param containerFileId
   */
  async deleteContainerFiles(containerFileId: number) {
    return await axiosInstance.delete(`/containers/files/${containerFileId}`)
  }
}
